import React, { useState, useEffect, useMemo } from 'react';
import { userService, eventService } from '../services/api';
import { Event } from '../types/event';
import { IconCalendar, IconBookmark, IconHeart, IconList, IconChecklist, IconAlertCircle } from '@tabler/icons-react';
import styles from './CalendarPage.module.css';
import EventCard from '../components/EventCard';

// Types de filtres disponibles
enum EventFilterType {
  ALL = 'all',
  CREATED = 'created',
  INTERESTED = 'interested',
  SAVED = 'saved'
}

// Jours de la semaine en français
const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

// Mois en français
const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

// Formater la distance pour l'affichage
const formatDistance = (distance?: number): string | undefined => {
  if (distance === undefined) return undefined;
  
  if (distance < 1) {
    // Moins d'1 km, afficher en mètres
    return `${Math.round(distance * 1000)}m`;
  } else if (distance < 10) {
    // Entre 1 et 10 km, afficher avec 1 décimale
    return `${distance.toFixed(1)}km`;
  } else {
    // Plus de 10 km, nombre entier
    return `${Math.round(distance)}km`;
  }
};

const CalendarPage: React.FC = () => {
  // Date du jour pour initialisation
  const today = new Date();
  
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isOrganization, setIsOrganization] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [activeFilter, setActiveFilter] = useState<EventFilterType>(EventFilterType.SAVED);
  const [currentDate, setCurrentDate] = useState<Date>(today);
  const [selectedDate, setSelectedDate] = useState<Date | null>(today);

  // Récupérer les informations de l'utilisateur courant
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const userData = await userService.getCurrentUser();
        
        // Vérifier si c'est une organisation ou un admin
        setIsOrganization(userData?.profileType === 'organization');
        setIsAdmin(userData?.role === 'ADMIN');
        
        // Définir le filtre par défaut selon le type d'utilisateur
        if (userData?.profileType === 'organization') {
          setActiveFilter(EventFilterType.CREATED);
        } else {
          setActiveFilter(EventFilterType.SAVED);
        }
      } catch (err) {
        console.error("Erreur lors de la récupération de l'utilisateur:", err);
      }
    };
    
    getCurrentUser();
  }, []);

  // Récupération des événements en fonction du filtre actif
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        let fetchedEvents: Event[] = [];

        // Récupérer tous les événements pertinents selon le filtre
        if (activeFilter === EventFilterType.ALL || activeFilter === EventFilterType.CREATED) {
          const organizationEvents = await eventService.getAll();
          // Marquer les événements créés
          const markedEvents = organizationEvents.map((event: Event) => ({
            ...event,
            isCreated: true
          })) as Event[];
          fetchedEvents = [...fetchedEvents, ...markedEvents];
        }

        if (activeFilter === EventFilterType.ALL || activeFilter === EventFilterType.INTERESTED) {
          const interestedEvents = await userService.getInterestedEvents();
          fetchedEvents = [...fetchedEvents, ...interestedEvents.filter(
            // Éviter les doublons
            (e: Event) => !fetchedEvents.some(fe => fe.id === e.id)
          )];
        }

        if (activeFilter === EventFilterType.ALL || activeFilter === EventFilterType.SAVED) {
          const savedEvents = await userService.getSavedEvents();
          fetchedEvents = [...fetchedEvents, ...savedEvents.filter(
            // Éviter les doublons
            (e: Event) => !fetchedEvents.some(fe => fe.id === e.id)
          )];
        }

        setEvents(fetchedEvents);
        setError(null);
      } catch (err) {
        console.error('Erreur lors du chargement des événements:', err);
        setError('Impossible de récupérer les événements pour le calendrier.');
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [activeFilter]);

  // Générer les jours du mois actuel
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    // Premier jour du mois
    const firstDay = new Date(year, month, 1);
    // Dernier jour du mois
    const lastDay = new Date(year, month + 1, 0);
    
    // Déterminer le jour de la semaine du premier jour (0 = dimanche)
    let firstDayOfWeek = firstDay.getDay();
    // Convertir pour que lundi = 0, dimanche = 6
    firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    
    // Tableau pour tous les jours à afficher
    const days = [];
    
    // Ajouter les jours vides avant le début du mois
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push({ date: null, events: [] });
    }
    
    // Ajouter tous les jours du mois avec leurs événements
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const date = new Date(year, month, i);
      const dayEvents = events.filter((event: Event) => {
        const eventDate = new Date(event.dateTime);
        return eventDate.getDate() === i &&
               eventDate.getMonth() === month &&
               eventDate.getFullYear() === year;
      });
      
      days.push({ date, events: dayEvents });
    }
    
    return days;
  }, [currentDate, events]);

  // Événements pour la date sélectionnée
  const selectedDateEvents = useMemo(() => {
    if (!selectedDate) return [];
    
    return events.filter((event: Event) => {
      const eventDate = new Date(event.dateTime);
      return eventDate.getDate() === selectedDate.getDate() &&
             eventDate.getMonth() === selectedDate.getMonth() &&
             eventDate.getFullYear() === selectedDate.getFullYear();
    });
  }, [selectedDate, events]);

  // Changer de mois
  const changeMonth = (increment: number) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + increment);
    setCurrentDate(newDate);
    
    // Ne pas réinitialiser la date sélectionnée si on est dans le même mois que la date sélectionnée
    if (selectedDate && 
        (selectedDate.getMonth() !== newDate.getMonth() || 
         selectedDate.getFullYear() !== newDate.getFullYear())) {
      setSelectedDate(null);
    }
  };

  // Formater l'en-tête du mois actuel
  const formattedMonthYear = useMemo(() => {
    return `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
  }, [currentDate]);

  // Gérer la sélection d'un jour
  const handleDayClick = (day: { date: Date | null, events: Event[] }) => {
    if (day.date) {
      setSelectedDate(day.date);
    }
  };

  // Revenir à aujourd'hui
  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
  };

  // Gérer le clic sur l'intérêt pour un événement
  const handleInterestClick = async (eventId: number) => {
    try {
      await eventService.toggleInterest(eventId);
      
      // Mettre à jour l'état local
      setEvents(prevEvents => 
        prevEvents.map((event: Event) => {
          if (event.id === eventId) {
            const newInterested = !event.isInterested;
            const currentInterests = event._count?.interests || 0;
            const currentSaved = event._count?.savedBy || 0;
            const currentViews = event._count?.views || 0;
            
            return {
              ...event,
              isInterested: newInterested,
              _count: {
                interests: newInterested ? currentInterests + 1 : Math.max(0, currentInterests - 1),
                savedBy: currentSaved,
                views: currentViews
              }
            };
          }
          return event;
        }) as Event[]
      );
    } catch (err) {
      console.error('Erreur lors du toggle d\'intérêt:', err);
    }
  };

  // Gérer le clic sur la sauvegarde d'un événement
  const handleSaveClick = async (eventId: number) => {
    try {
      await eventService.toggleSave(eventId);
      
      // Mettre à jour l'état local
      setEvents(prevEvents => 
        prevEvents.map((event: Event) => {
          if (event.id === eventId) {
            const newSaved = !event.isSaved;
            const currentInterests = event._count?.interests || 0;
            const currentSaved = event._count?.savedBy || 0;
            const currentViews = event._count?.views || 0;
            
            return {
              ...event,
              isSaved: newSaved,
              _count: {
                interests: currentInterests,
                savedBy: newSaved ? currentSaved + 1 : Math.max(0, currentSaved - 1),
                views: currentViews
              }
            };
          }
          return event;
        }) as Event[]
      );
    } catch (err) {
      console.error('Erreur lors du toggle de sauvegarde:', err);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>
          <IconCalendar size={24} />
          Calendrier des événements
        </h1>
        <p className={styles.subtitle}>
          Visualisez et organisez tous vos événements dans un calendrier.
        </p>
      </div>

      <div className={styles.filterBar}>
        <button 
          className={`${styles.filterButton} ${activeFilter === EventFilterType.ALL ? styles.active : ''}`}
          onClick={() => setActiveFilter(EventFilterType.ALL)}
        >
          <IconList size={16} />
          Tous
        </button>
        
        {/* Option "Créés" visible uniquement pour les organisations et admins */}
        {(isOrganization || isAdmin) && (
          <button 
            className={`${styles.filterButton} ${activeFilter === EventFilterType.CREATED ? styles.active : ''}`}
            onClick={() => setActiveFilter(EventFilterType.CREATED)}
          >
            <IconChecklist size={16} />
            Créés
          </button>
        )}
        
        <button 
          className={`${styles.filterButton} ${activeFilter === EventFilterType.INTERESTED ? styles.active : ''}`}
          onClick={() => setActiveFilter(EventFilterType.INTERESTED)}
        >
          <IconHeart size={16} />
          Intéressé
        </button>
        
        <button 
          className={`${styles.filterButton} ${activeFilter === EventFilterType.SAVED ? styles.active : ''}`}
          onClick={() => setActiveFilter(EventFilterType.SAVED)}
        >
          <IconBookmark size={16} />
          Sauvegardés
        </button>
      </div>

      {loading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p className={styles.loadingText}>Chargement des événements...</p>
        </div>
      ) : error ? (
        <div className={styles.errorContainer}>
          <IconAlertCircle size={24} />
          <p className={styles.errorText}>{error}</p>
        </div>
      ) : (
        <div className={styles.calendarLayout}>
          <div className={styles.calendarWrapper}>
            <div className={styles.calendarContainer}>
              <div className={styles.calendarHeader}>
                <button 
                  className={styles.monthNavButton} 
                  onClick={() => changeMonth(-1)}
                >
                  &lt;
                </button>
                <h2 className={styles.monthTitle}>{formattedMonthYear}</h2>
                <button 
                  className={styles.monthNavButton} 
                  onClick={() => changeMonth(1)}
                >
                  &gt;
                </button>
              </div>

              <div className={styles.calendarControls}>
                <button 
                  className={styles.todayButton}
                  onClick={goToToday}
                >
                  Aujourd'hui
                </button>
              </div>

              <div className={styles.weekdaysHeader}>
                {WEEKDAYS.map((day: string) => (
                  <div key={day} className={styles.weekdayCell}>{day}</div>
                ))}
              </div>

              <div className={styles.calendar}>
                {calendarDays.map((day: { date: Date | null; events: Event[] }, index: number) => {
                  // Déterminer si c'est le jour courant
                  const isToday = day.date && 
                    day.date.getDate() === today.getDate() &&
                    day.date.getMonth() === today.getMonth() &&
                    day.date.getFullYear() === today.getFullYear();
                  
                  // Déterminer si c'est le jour sélectionné
                  const isSelected = day.date && selectedDate && 
                    day.date.getDate() === selectedDate.getDate() && 
                    day.date.getMonth() === selectedDate.getMonth() &&
                    day.date.getFullYear() === selectedDate.getFullYear();
                  
                  return (
                    <div 
                      key={index} 
                      className={`${styles.calendarDay} 
                        ${!day.date ? styles.emptyDay : ''} 
                        ${isSelected ? styles.selectedDay : ''}
                        ${isToday ? styles.todayDay : ''}`}
                      onClick={() => handleDayClick(day)}
                    >
                      {day.date && (
                        <>
                          <span className={styles.dayNumber}>{day.date.getDate()}</span>
                          {day.events.length > 0 && (
                            <div className={styles.eventDots}>
                              {day.events.slice(0, 3).map((event: Event, i: number) => (
                                <span 
                                  key={i} 
                                  className={`${styles.eventDot} ${
                                    event.isCreated ? styles.createdEvent : 
                                    event.isInterested ? styles.interestedEvent : 
                                    event.isSaved ? styles.savedEvent : ''
                                  }`} 
                                  title={event.title}
                                ></span>
                              ))}
                              {day.events.length > 3 && (
                                <span className={styles.moreEvents}>+{day.events.length - 3}</span>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className={styles.eventsWrapper}>
            {selectedDate ? (
              <div className={styles.selectedDateEvents}>
                <h3 className={styles.selectedDateTitle}>
                  Événements du {selectedDate.getDate()} {MONTHS[selectedDate.getMonth()]} {selectedDate.getFullYear()}
                </h3>
                {selectedDateEvents.length > 0 ? (
                  <div className={styles.eventsList}>
                    {selectedDateEvents.map((event: Event) => (
                      <EventCard 
                        key={event.id} 
                        event={event} 
                        onClick={() => window.location.href = `/events/${event.id}`}
                        onInterestClick={handleInterestClick}
                        onSaveClick={handleSaveClick}
                        isOrganizationDashboard={isOrganization}
                        distanceLabel={formatDistance(event.distance)}
                      />
                    ))}
                  </div>
                ) : (
                  <p className={styles.noEventsMessage}>Aucun événement pour cette date.</p>
                )}
              </div>
            ) : (
              <div className={styles.selectedDateEvents}>
                <h3 className={styles.selectedDateTitle}>
                  Événements
                </h3>
                <p className={styles.noEventsMessage}>Sélectionnez une date pour voir les événements.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPage;
